import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { Spinner } from '../Spinner/Spinner';

// Button variants
type ButtonVariant =
	| 'primary'
	| 'secondary'
	| 'outline'
	| 'danger'
	| 'success'
	| 'link'
	| 'dark';

// Button sizes
type ButtonSize = 'small' | 'medium' | 'large';

type ButtonShape = 'circle' | 'rounded';

// Props interface
interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: ButtonVariant;
	size?: ButtonSize;
	fullWidth?: boolean;
	leftIcon?: ReactNode;
	rightIcon?: ReactNode;
	children?: ReactNode;
	className?: string;
	shape?: ButtonShape;
	loading?: boolean;
}

export const Button = ({
	variant = 'primary',
	size = 'medium',
	fullWidth = false,
	leftIcon,
	rightIcon,
	children,
	className,
	disabled,
	shape = 'rounded',
	loading = false,
	...props
}: ButtonProps) => {
	// Base styles
	const baseStyles =
		'inline-flex items-center justify-center transition-colors duration-200 font-medium cursor-pointer';

	// Width styles
	const widthStyles = fullWidth ? 'w-full' : '';
	const loadingStyles = loading ? 'gap-2' : '';

	// Size styles
	const sizeStyles = {
		small: 'px-5 py-2 text-sm',
		medium: 'px-8 py-3 text-md',
		large: 'px-10 py-4 text-xl',
	}[size];

	// Variant styles
	const variantStyles = {
		primary:
			'bg-green text-secondary-black hover:bg-green-400 focus:ring-green-light',
		secondary: 'bg-grey-2 text-secondary-black hover:bg-grey-3',
		outline: 'border border-grey-3 text-secondary-black hover:bg-green-200',
		danger: 'bg-red text-white hover:bg-red-600',
		success: 'bg-green text-secondary-black hover:bg-green-dark',
		dark: 'bg-base-black text-white hover:bg-black-01',
		link: 'bg-transparent text-green-600 p-0',
	}[variant];

	// Disabled styles
	const disabledStyles = disabled ? 'opacity-50 cursor-not-allowed' : '';

	const borderRadiusStyles = {
		small: 'rounded-4xl',
		medium: 'rounded-4xl',
		large: 'rounded-4xl',
	}[size];

	const shapeStyles = {
		circle:
			'rounded-full px-4 py-4 items-center justify-center hover:bg-green-400',
		rounded: 'rounded-4xl',
	}[shape];

	// Combine all styles
	const buttonStyles = twMerge(
		baseStyles,
		widthStyles,
		sizeStyles,
		variantStyles,
		disabledStyles,
		borderRadiusStyles,
		shapeStyles,
		loadingStyles,
		className,
	);

	return (
		<button className={buttonStyles} disabled={disabled} {...props}>
			{loading && <Spinner size={size} />}
			{leftIcon && (
				<span className={shape === 'circle' ? '' : 'mr-2'}>{leftIcon}</span>
			)}
			{children}
			{rightIcon && (
				<span className={shape === 'circle' ? '' : 'ml-2'}>{rightIcon}</span>
			)}
		</button>
	);
};
